import { type CascaderOption, Flex, Select, Space, Text } from '@/components';
import { usePoliciesOptions } from '@/features/master-table/policy/_hooks/policy';
import {
  useCreateQueryStore,
  useQueryStore,
} from '@/features/search/_hooks/query-store';
import { useProjectsSearchStore } from '@/features/search/_stores/projects-search-store';
import { generateQueryAndLeafs } from '@/features/search/_utils/query-store';
import { isPS } from '@/utils';
import { type FC, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import {
  type AnalysisPageSearchQueryStates,
  useAnalysisPageSearchQueryStates,
} from '../../_hooks/search-query-states';
import {
  GraphTypeLabelModifier,
  type ProjectAnalysisModels,
  graphTypeEnum,
} from '../../_models/project';
import { AnalysisGraphBreadcrumbs } from './AnalysisGraphBreadcrumbs';
import { FilterHistoryBackButton } from './FilterHistoryBackButton';
import { VisualizerContent } from './visualizer/VisualizerContent';

const graphTypeOptions = graphTypeEnum.options.flatMap((value) => {
  return {
    value,
    label: GraphTypeLabelModifier[value],
  };
});

export const AnalysisVisualizer: FC<{
  graphData: z.infer<typeof ProjectAnalysisModels>;
}> = memo(({ graphData }) => {
  const { t } = useTranslation();
  const { searchQueryStates, setSearchQueryStates } =
    useAnalysisPageSearchQueryStates();
  const { cascaderTargetIds: currentCascaderValues, data } =
    useProjectsSearchStore();

  const { graphType, cascader, organizationIds, policyIds, analysisAxis } =
    searchQueryStates;

  const { mutateAsync } = useCreateQueryStore();

  const treeParamsData = useQueryStore(cascader, !!cascader);

  // 組織軸の処理
  const historyOrganizationIds = isPS()
    ? (organizationIds ?? [])
    : (treeParamsData.data?.query?.organizationIds?.flatMap(
        (v) => v.at(-1) ?? [],
      ) ?? []);

  const organizationsOptions = getCurrentDrillDownOptions({
    historyIds: historyOrganizationIds,
    options: data.organizationsOptions ?? [],
  });

  // 政策・施策軸の処理
  const historyPolicyIds = isPS()
    ? (policyIds ?? [])
    : (treeParamsData.data?.query?.policyIds?.flatMap((v) => v.at(-1) ?? []) ??
      []);

  const policiesOptionsQuery = usePoliciesOptions();

  const policiesOptions = getCurrentDrillDownOptions({
    historyIds: historyPolicyIds,
    options: policiesOptionsQuery.data ?? [],
  });

  const handleGraphInteraction = useCallback(
    async (ids: string[]) => {
      if (analysisAxis === 'policy') {
        // 政策・施策軸の場合
        const newQueryAndLeafs = generateQueryAndLeafs({
          organizationIds: currentCascaderValues.organizationIds,
          otherResponsibleOrganizationIds:
            currentCascaderValues.otherResponsibleOrganizationIds,
          policyIds: ids.length ? [ids] : null,
          expenditureBudgetPurposeIds:
            currentCascaderValues.expenditureBudgetPurposeIds,
        });

        const nextParams: AnalysisPageSearchQueryStates = {
          ...searchQueryStates,
          policyIds: isPS() && ids.length ? [ids.at(-1) ?? ''] : null,
          cascader:
            !isPS() && ids.length && newQueryAndLeafs
              ? ((await mutateAsync(newQueryAndLeafs))?.id ?? null)
              : null,
        };

        setSearchQueryStates((prev) => ({ ...prev, ...nextParams }));
      } else {
        // 組織軸の場合（既存のロジック）
        const newQueryAndLeafs = generateQueryAndLeafs({
          organizationIds: ids.length ? [ids] : null,
          otherResponsibleOrganizationIds:
            currentCascaderValues.otherResponsibleOrganizationIds,
          policyIds: currentCascaderValues.policyIds,
          expenditureBudgetPurposeIds:
            currentCascaderValues.expenditureBudgetPurposeIds,
        });

        const nextParams: AnalysisPageSearchQueryStates = {
          ...searchQueryStates,
          organizationIds: isPS() && ids.length ? [ids.at(-1) ?? ''] : null,
          cascader:
            !isPS() && ids.length && newQueryAndLeafs
              ? ((await mutateAsync(newQueryAndLeafs))?.id ?? null)
              : null,
        };

        setSearchQueryStates((prev) => ({ ...prev, ...nextParams }));
      }
    },
    [
      analysisAxis,
      mutateAsync,
      searchQueryStates,
      setSearchQueryStates,
      currentCascaderValues,
    ],
  );

  // 分析軸に応じて適切なオプションとIDを選択
  const currentOptions =
    analysisAxis === 'policy' ? policiesOptions : organizationsOptions;
  const currentHistoryIds =
    analysisAxis === 'policy' ? historyPolicyIds : historyOrganizationIds;

  return (
    <Flex vertical gap={16}>
      <Flex justify="space-between" align="center">
        <FilterHistoryBackButton disabled={!currentOptions?.ids.length} />
        <Space direction="vertical" size="small">
          <Text text={t('graph-type')} />
          <Select
            value={graphType}
            options={graphTypeOptions}
            onChange={(type) => {
              setSearchQueryStates((prev) => ({ ...prev, graphType: type }));
            }}
            style={{ width: 230 }}
          />
        </Space>
      </Flex>
      <Flex vertical gap={16}>
        <AnalysisGraphBreadcrumbs
          hideBreadcrumb={
            // 検索で複数の組織/政策を選択する可能性があるため
            isPS()
              ? currentHistoryIds?.length !== 1
              : analysisAxis === 'policy'
                ? treeParamsData.data?.query?.policyIds?.length !== 1
                : treeParamsData.data?.query?.organizationIds?.length !== 1
          }
          options={currentOptions}
          handleGraphInteraction={handleGraphInteraction}
          analysisAxis={analysisAxis}
        />
        <VisualizerContent
          graphData={graphData}
          options={currentOptions}
          handleGraphInteraction={handleGraphInteraction}
        />
      </Flex>
    </Flex>
  );
});
AnalysisVisualizer.displayName = 'AnalysisVisualizer';

const findUpperRecursive = (
  targetId: string,
  organizations: CascaderOption[],
): { names: string[]; ids: string[] } => {
  for (const org of organizations) {
    if (org.value === targetId) {
      return {
        names: [org.label],
        ids: [org.value],
      };
    }
    if (org.children) {
      const result = findUpperRecursive(targetId, org.children);
      if (result.ids.length > 0) {
        return {
          names: [org.label, ...result.names],
          ids: [org.value.toString(), ...result.ids],
        };
      }
    }
  }
  return { names: [], ids: [] };
};

export const getOrganizationNamesWithUpper = ({
  id,
  organizations,
}: {
  id: string | undefined;
  organizations: CascaderOption[];
}) => {
  if (!id) {
    return { names: [], ids: [] };
  }
  return findUpperRecursive(id, organizations);
};

/**
 * グラフのドリルダウン状態（パンくず・currentId）を表す options を返す。
 * 検索で複数の組織/政策を選択している場合は特定の「現在地」が存在しないため
 * undefined を返し、ちょうど 1 件のパスに絞り込まれている場合のみその階層を返す。
 */
export const getCurrentDrillDownOptions = ({
  historyIds,
  options,
}: {
  historyIds: string[];
  options: CascaderOption[];
}): { names: string[]; ids: string[] } | undefined => {
  if (historyIds.length !== 1) {
    return undefined;
  }
  return getOrganizationNamesWithUpper({
    id: historyIds.at(0),
    organizations: options,
  });
};
